import React, { memo, useCallback, useMemo } from 'react';
import classNames from 'classnames';
import { DragDropContext, OnDragEndResponder } from '@hello-pangea/dnd';
import _get from 'lodash/get';
import _omit from 'lodash/omit';
import _noop from 'lodash/noop';
import CircleLoaderWrapper, {
  CircleLoaderWrapperType,
} from '@lib/components/CircleLoaderWrapper/CircleLoaderWrapper';
import SupportiveMessage, {
  SupportiveMessageColor,
} from '@lib/components/SupportiveMessage/SupportiveMessage';
import { ErrorsKey, FormProps } from './types';
import logSubmitError from './utils/logSubmitError';
import { useFieldArrayContext } from './FormProvider';
import styles from './Form.module.scss';

/**
 * Presentational form wrapper that renders form items and action slots while keeping
 * React Hook Form concerns at arm's length.
 */
function Form(props: FormProps) {
  const {
    actions,
    actionsComponent: ActionsComponent,
    actionsComponentProps,
    autoComplete = 'off',
    fieldsArray,
    formClassName,
    formId,
    formItemsClassName,
    formState,
    handleSubmit,
    loading = false,
    loadingDesc,
    isDirtyOverride,
    onSubmitHandler,
    renderAfterForm,
    sourceFields,
    stepsCount,
  } = props;
  const { errors } = formState;
  const isDirty = isDirtyOverride ?? formState.isDirty;
  const generalError = _get(errors, [ErrorsKey.General, 'message']);

  const { fieldArrayMethodsByName } = useFieldArrayContext();
  const submitHandler = useMemo(() => {
    const rhfSubmit = handleSubmit(onSubmitHandler);
    return (e?: React.BaseSyntheticEvent) => rhfSubmit(e).catch(logSubmitError);
  }, [handleSubmit, onSubmitHandler]);

  const handleDrag: OnDragEndResponder = useCallback(
    (result) => {
      const { source, destination, draggableId, reason } = result;
      const destinationIndex = destination?.index;
      const destinationDroppableId = destination?.droppableId;
      const sourceDroppableId = source?.droppableId;
      const fieldName = destinationDroppableId?.split('-')[1];
      if (fieldName && reason === 'DROP') {
        const fieldArrayMethods = _get(
          fieldArrayMethodsByName,
          fieldName,
          undefined,
        );
        const handleMove = _get(fieldArrayMethods, 'move', _noop);
        const handleInsert = _get(fieldArrayMethods, 'insert', _noop);
        const getProtectedIndices = _get(
          fieldArrayMethods,
          'getProtectedIndices',
          undefined,
        );

        // Check if destination would affect a protected position
        const protectedIndices = getProtectedIndices?.() ?? [];
        const isDestinationProtected = protectedIndices.includes(
          destinationIndex ?? -1,
        );

        if (destinationIndex !== undefined) {
          if (destinationDroppableId === sourceDroppableId) {
            // Block move if destination is a protected position
            if (handleMove && !isDestinationProtected) {
              handleMove(source.index, destinationIndex);
            }
          } else if (
            destinationDroppableId === 'FieldArrayDroppable-questions' &&
            sourceDroppableId === 'FieldArrayDroppable-questions-source'
          ) {
            const subForm = sourceFields?.questions?.find(
              ({ id }) => id === draggableId,
            );
            if (subForm) {
              handleInsert(
                destinationIndex,
                _omit(subForm, 'renderCustomIcon', 'nameTranslationKey'),
              );
            }
          }
        }
      }
      return result;
    },
    [fieldArrayMethodsByName, sourceFields?.questions],
  );

  return (
    <DragDropContext onDragEnd={handleDrag}>
      {}
      <form
        onSubmit={submitHandler}
        className={formClassName}
        id={formId}
        autoComplete={autoComplete}
        data-test-id="form__root"
      >
        <CircleLoaderWrapper
          type={CircleLoaderWrapperType.blur}
          isLoading={loading}
          description={loadingDesc}
          rootClassName={styles.circleLoaderWrapper}
        >
          <div
            className={classNames(styles.formItems, formItemsClassName)}
            data-test-id="form__fields"
          >
            {fieldsArray.map((field) => field)}
          </div>
          {generalError && typeof generalError === 'string' && (
            <SupportiveMessage
              text={generalError}
              color={SupportiveMessageColor.Red}
            />
          )}
        </CircleLoaderWrapper>
        {actions !== undefined && (
          <div>
            {actions({
              // @ts-ignore
              errors,
              isDirty,
              loading,
              onSubmitForm: submitHandler,
            })}
          </div>
        )}
        {ActionsComponent !== undefined && (
          <ActionsComponent
            {...(actionsComponentProps || {})}
            stepsCount={stepsCount}
            isDirty={isDirty}
            loading={loading}
          />
        )}
      </form>
      {renderAfterForm !== undefined && renderAfterForm()}
    </DragDropContext>
  );
}

export default memo(Form);
